import type {
  PaymentMethodOption,
  PaymentMethodSelectorProps,
} from '@engyalo/delivery-ui-components'
import { CheckoutRules } from '../interfaces'
import type { ICheckoutRuleValue } from '../graphQL/queries/getCheckoutRule/types'
import type { RootState } from '../redux/store'
import { selectRulePickId } from './checkoutRuleSelection'
import flatTexts from './flatTexts'
import { isHidePricesEnabled } from './hidePrices'

const TERM_DAYS_PLACEHOLDER = /\{\{\s*termDays\s*\}\}/g

/**
 * The panel props this adapter owns, checked against the library's own prop
 * shape.
 *
 * The same guard the three shipped panels use, catching the same drift: the
 * injector is registered in a `Map` whose value type is `UnresolvedRecord`, so
 * a library rename of `selectedMethodId` would compile, land unread on the
 * picker, and silently stop highlighting the shopper's choice.
 *
 * **It guards names and value types, not presence.** Every prop here is
 * optional, so `{}` satisfies this type — dropping a line from the returned
 * object is a compile *success*. The colocated assertions cover that instead,
 * which is why each forwarded prop has a test naming it.
 *
 * `onMethodSelect` is deliberately absent, and it is the picker's one
 * **required** prop. It is an authored action, not derived state: config binds
 * it to `templateActions.paymentMethod.setPaymentMethod` through
 * `cinnamonActions`, and `processActions` turns that into the callable the
 * library receives. Injecting a function here would take the storefront's
 * ability to author it away for no gain.
 */
type InjectedPaymentMethodProps = Pick<
  PaymentMethodSelectorProps,
  'methods' | 'selectedMethodId' | 'hidePrices'
>

/**
 * The wire's options, shaped into the rows the picker draws.
 *
 * **The wire carries no stable id for a keyed list.** `getCheckoutRule`
 * returns `{ data, amount?, key?, attributes? }` entries (COM-1072). `data`
 * is the backend enum value — `CASH`, `CREDIT`, `TRANSFER`, `YALO_PAGO` —
 * and `key` identifies two options whose `data` repeats (two `CREDIT`
 * entries with different terms, say). The picker wants `{ id, method }`,
 * and the id has to be unique across rows, so it is `key ?? data`:
 * `key` where the list is keyed, `data` where it is not. `method` stays the
 * backend enum value so `paymentMethodLabels` and every downstream reader
 * keep seeing `CREDIT`, not `CREDIT_14D`.
 *
 * `amount` is forwarded when `Number.isFinite`, as a shopper-facing surcharge
 * beside the method label (COM-1251 on the library side, COM-1257 here). The
 * guard drops `undefined`, `null`, `NaN` and `±Infinity`; it keeps `0`, which
 * the library renders as nothing by its own rule. On `PAYMENT_METHODS` the
 * amount is authored per option (guide §4), copied from the offered option
 * onto the pick by the server; `DELIVERY_DATES` still arrives `0` from a
 * resolver that hard-codes it.
 *
 * ⚠️ **`hidePrices` is the only thing suppressing the amount from a
 * hide-prices customer.** `maskPricesForDisplay` is an allowlist of price-shaped
 * *config* keys, where a bare number is left alone on purpose because an
 * authored amount is behaviour data; it does not zero the `amount` on a
 * payment-method option. The library gates the render on `hidePrices` +
 * `currency` itself, so the `hidePrices` prop below now carries this gate as
 * well as the free-text one.
 *
 * `attributes.label` and `attributes.helperText` are forwarded to the picker's
 * own `label` and `helperText` props. Yao's guide (§6): render as plain text,
 * never HTML or markdown — the store authors them and nothing sanitises them
 * server-side. The library's `<Text>` and MUI components treat input as text,
 * which is what the smoke test in
 * `utils/tests/paymentMethodSelector.test.ts` pins.
 *
 * `attributes.termDays` is a positive integer (COM-1254). Where present it is
 * substituted into `tagTermDaysTemplate` — the storefront's
 * `texts.paymentMethod.tagTermDays`, resolved by `getPaymentMethodSelectorProps`
 * so this function stays state-free — and the result is forwarded as the row's
 * `tag`. The template is plural-always on purpose: no locale here distinguishes
 * `1 día` from `N días`, matching the fleet-wide `{{var}}` + `String.replace`
 * convention `minAmountProgressMessage` and `formatPromotionMessage` already
 * use. Values that are not a positive integer, and an absent or empty template,
 * omit the tag.
 *
 * Everything else inside `attributes` stays untouched.
 *
 * An entry with no usable `data` is dropped rather than mapped. The array
 * arrives from untyped JSON by way of a checkout rule, so a `null` in it is
 * reachable and `null.data` throws — a blank screen over a working storefront.
 *
 * ⚠️ **The picker's row count can still be lower than this one, and cinnamon
 * cannot predict by how much.** `buildMethodRows` drops duplicate ids and any
 * row whose resolved label is empty — and a label resolves through
 * `paymentMethodLabels`, which is authored config this adapter never sees.
 * Mapping a method to `''` there withdraws it from the picker deliberately. So
 * a storefront can suppress every option and leave
 * `checkout.ui.PAYMENT_METHODS.emptyHidden` reading `true` over an empty
 * screen. Exact agreement is impossible by construction, not merely unbuilt;
 * do not reimplement the library's drop rules here chasing it.
 */
export const toPaymentMethodOptions = (
  values?: ICheckoutRuleValue[] | null,
  tagTermDaysTemplate?: string,
): PaymentMethodOption[] | undefined => {
  if (!Array.isArray(values)) return undefined

  return values
    .filter((value): value is ICheckoutRuleValue => !!value?.data)
    .map((value) => {
      const { label, helperText, termDays } = value.attributes ?? {}
      const canTagTermDays =
        typeof tagTermDaysTemplate === 'string' &&
        tagTermDaysTemplate.length > 0 &&
        typeof termDays === 'number' &&
        Number.isInteger(termDays) &&
        termDays > 0
      return {
        id: value.key ?? value.data,
        method: value.data,
        ...(Number.isFinite(value.amount) ? { amount: value.amount } : {}),
        ...(typeof label === 'string' ? { label } : {}),
        ...(typeof helperText === 'string' ? { helperText } : {}),
        ...(canTagTermDays
          ? {
              tag: (tagTermDaysTemplate as string).replace(
                TERM_DAYS_PLACEHOLDER,
                String(termDays),
              ),
            }
          : {}),
      }
    })
}

/**
 * The three `PaymentMethodSelector` props cinnamon owns rather than the config
 * author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `PaymentMethodSelector`, exactly as `getCartCheckoutPanelProps` is for the
 * cart. None is authorable as a `{{variable}}`: two are shapes the config
 * language cannot build, and one is a predicate it cannot compute.
 *
 * Everything else stays in config — `paymentLabel`, `paymentMethodLabels`, the
 * per-option `tag` and `helperText`, every colour prop, `fieldsToDisplay`, the
 * two custom slots, and `onMethodSelect`. This file adds no layout or copy
 * decision.
 *
 * **`methods` is `undefined` before a fetch, not `[]`.** Both draw nothing —
 * the picker returns `null` with no rows — but the distinction is what keeps
 * the empty-state message off a loading screen, and it is `emptyHidden` on the
 * slice that carries it to config.
 */
export const getPaymentMethodSelectorProps = (
  state: RootState,
): InjectedPaymentMethodProps => ({
  methods: toPaymentMethodOptions(
    state.checkoutSlice.available[CheckoutRules.PAYMENT_METHODS]?.data,
    flatTexts(state?.defaultSlice?.config?.texts?.paymentMethod).tagTermDays,
  ),
  /**
   * The shopper's pick, as the picker's `id` — `key ?? data`, matching the
   * `id` `toPaymentMethodOptions` builds for each row.
   *
   * `selectRulePickId` reads the slice first (this session's pick) and the
   * session's `checkoutRules` behind it (the last confirmed pick: the boot
   * response's, or the last landed write's since COM-1765), returning
   * `key ?? data` on both sides. The slice-first order is the one
   * `selectRulePick` carries for the sibling `data`-only reader — a session
   * read in front of a fresh write would show the previous pick until that
   * write lands.
   *
   * The session fallback is why reopening the webview with a saved keyed
   * pick highlights the right row (COM-1253): the slice is empty on reload,
   * so the previous read had nothing to return. On a non-keyed list and on
   * persisted picks that predate PR 2's keyed shape, `key` is absent on both
   * sides and the id degenerates to `data` — same backend enum value today's
   * callers already match on.
   *
   * An id outside `methods` selects nothing, which the library calls the
   * honest rendering of a session carrying a method the storefront no longer
   * offers. That is reachable here: the selection survives a refetch that
   * withdrew it.
   */
  selectedMethodId: selectRulePickId(state, CheckoutRules.PAYMENT_METHODS),
  /**
   * The flag gates two different renders, and the second was reached only
   * after COM-1257. The first is the free-text one that was always true:
   * `tag`, `helperText` and the two custom slots take authored strings, and
   * `maskPricesForDisplay` is an allowlist of prop *names* over *numbers*, so
   * it cannot see the price inside `'CASH — $50 de recargo'`. The second is
   * `PaymentMethodOption.amount`, now forwarded above: the library gates its
   * own render on this flag, since `maskPricesForDisplay` does not zero it
   * either.
   */
  hidePrices: isHidePricesEnabled(state),
})

export default getPaymentMethodSelectorProps
